import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Modal,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Col,
  Table,
  Spinner,
} from "reactstrap";

import { legendsRepo } from "data/repositories/legendsRepo";
import { auth, db } from "data/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getAuthUser } from "components/Auth/authStore";
import { onAuthStateChanged } from "firebase/auth";

const APP_BASE_RAW = process.env.PUBLIC_URL || "";
const APP_BASE =
  APP_BASE_RAW && APP_BASE_RAW !== "/" ? APP_BASE_RAW.replace(/\/+$/, "") : "";

const LEGENDS_BANNER_ITEMS = [
  {
    src: `${APP_BASE}/banners/banner-1.jpeg`,
    altText: "Karadeniz Destanları - Banner 1",
    key: "banner-1",
  },
  {
    src: `${APP_BASE}/banners/banner-2.jpeg`,
    altText: "Karadeniz Destanları - Banner 2",
    key: "banner-2",
  },
  {
    src: `${APP_BASE}/banners/banner-3.jpeg`,
    altText: "Karadeniz Destanları - Banner 3",
    key: "banner-3",
  },
  {
    src: `${APP_BASE}/banners/banner-4.jpeg`,
    altText: "Karadeniz Destanları - Banner 4",
    key: "banner-4",
  },
  {
    src: `${APP_BASE}/banners/banner-5.jpeg`,
    altText: "Karadeniz Destanları - Banner 5",
    key: "banner-5",
  },
  {
    src: `${APP_BASE}/banners/banner-6.jpeg`,
    altText: "Karadeniz Destanları - Banner 6",
    key: "banner-6",
  },
  {
    src: `${APP_BASE}/banners/banner-7.jpeg`,
    altText: "Karadeniz Destanları - Banner 7",
    key: "banner-7",
  },
  {
    src: `${APP_BASE}/banners/banner-8.jpeg`,
    altText: "Karadeniz Destanları - Banner 8",
    key: "banner-8",
  },
];

const LEGEND_FALLBACK_THUMB_SRC = `${APP_BASE}/kadekon-logo.png`;
const LEGEND_ACTION_BUTTON_STYLE = {
  fontSize: 11,
  whiteSpace: "nowrap",
  lineHeight: 1.2,
  padding: "0.25rem 0.4rem",
};

export default function LegendsList({ embedded = false, mediaFilter = "all" } = {}) {
  const localUser = getAuthUser();
  const [firebaseUid, setFirebaseUid] = useState(auth.currentUser?.uid || "");
  const currentUid = firebaseUid || localUser?.id || "";
  const likeUid = firebaseUid || "";
  const [currentRole, setCurrentRole] = useState(localUser?.role || "");
  const isAdmin = String(currentRole || "").toLowerCase() === "admin";

  const buildLegendUrl = useCallback((id) => {
    const safeId = String(id || "").trim();
    if (!safeId) return "";
    const origin =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "";
    return `${origin}${APP_BASE}/admin/content/legends/view/${safeId}`;
  }, []);

  const onShareLegend = useCallback(
    async (legend) => {
      const url = buildLegendUrl(legend?.id);
      if (!url) return;

      const title = String(legend?.title || "Efsane").trim() || "Efsane";
      try {
        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
          await navigator.share({ title, url });
          return;
        }
      } catch {
        return;
      }

      try {
        if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          alert("Link kopyalandı.");
          return;
        }
      } catch {}

      try {
        window.prompt("Bağlantıyı kopyalayın:", url);
      } catch {}
    },
    [buildLegendUrl]
  );

  const [selectedProvince, setSelectedProvince] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [pendingApprovalFilter, setPendingApprovalFilter] = useState(false);

  const normalizedMediaFilter = String(mediaFilter || "all").toLowerCase();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isBannerPreviewOpen, setIsBannerPreviewOpen] = useState(false);
  const [bannerPreviewItem, setBannerPreviewItem] = useState(LEGENDS_BANNER_ITEMS[0]);
  const [isLegendPreviewOpen, setIsLegendPreviewOpen] = useState(false);
  const [legendPreviewItem, setLegendPreviewItem] = useState(null);
  const [legendPreviewAuthor, setLegendPreviewAuthor] = useState(null);
  const legendPreviewIdRef = useRef(null);
  const [likeBusyById, setLikeBusyById] = useState({});

  const pageSize = 10;
  const visibleItems = items.filter((item) => {
    if (isAdmin) return true;
    const isActive = item?.isActive !== false;
    if (isActive) return true;
    const ownerUid = item?.createdBy?.uid;
    return !!ownerUid && !!currentUid && ownerUid === currentUid;
  });

  const approvalFilteredItems = pendingApprovalFilter
    ? visibleItems.filter((item) => item?.isActive === false)
    : visibleItems;

  const mediaFilteredItems = approvalFilteredItems.filter((item) => {
    if (normalizedMediaFilter === "all") return true;
    if (normalizedMediaFilter === "image") return !!item?.imageUrl;
    if (normalizedMediaFilter === "audio") return !!item?.audioUrl;
    if (normalizedMediaFilter === "video") return !!(item?.videoUrl || item?.videoLink);
    return true;
  });

  const legendsTotalCount = mediaFilteredItems.length;
  const provinceRows = (() => {
    const counts = new Map();
    for (const item of mediaFilteredItems) {
      const province = String(item?.province || "").trim();
      if (!province) continue;
      counts.set(province, (counts.get(province) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([province, count]) => ({ province, count }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return String(a.province).localeCompare(String(b.province), "tr");
      });
  })();

  const provinceFilteredItems = selectedProvince
    ? mediaFilteredItems.filter(
        (item) => String(item?.province || "").trim() === selectedProvince
      )
    : mediaFilteredItems;

  const normalizeDistrictKey = (value) => {
    const trimmed = String(value || "").trim();
    return trimmed ? trimmed : "__none__";
  };

  const districtRows = (() => {
    if (!selectedProvince) return [];
    const counts = new Map();
    for (const item of provinceFilteredItems) {
      const key = normalizeDistrictKey(item?.district);
      counts.set(key, (counts.get(key) || 0) + 1);
    }

    return Array.from(counts.entries())
      .map(([districtKey, count]) => ({
        districtKey,
        districtLabel: districtKey === "__none__" ? "Belirtilmemiş" : districtKey,
        count,
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return String(a.districtLabel).localeCompare(String(b.districtLabel), "tr");
      });
  })();

  const districtFilteredItems = selectedDistrict
    ? provinceFilteredItems.filter(
        (item) => normalizeDistrictKey(item?.district) === selectedDistrict
      )
    : provinceFilteredItems;

  const totalPages = Math.max(1, Math.ceil(districtFilteredItems.length / pageSize));
  const pagedItems = districtFilteredItems.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const goToPage = (page) => {
    const nextPage = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(nextPage);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await legendsRepo.list();
      setItems(data);
    } catch (error) {
      console.error("Error loading legends:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setFirebaseUid(u?.uid || "");
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!currentUid || String(currentUid).startsWith("phone:")) return;
      try {
        const snap = await getDoc(doc(db, "users", currentUid));
        const role = snap.exists() ? snap.data()?.role : null;
        if (!cancelled && role) setCurrentRole(role);
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUid]);

  useEffect(() => {
    setCurrentPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [normalizedMediaFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProvince]);

  useEffect(() => {
    setSelectedDistrict("");
  }, [selectedProvince]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDistrict]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pendingApprovalFilter]);

  const onToggleActive = async (item) => {
    const newStatus = !item.isActive;
    if (newStatus && item?.approved !== true) {
      alert("Bu içeriği aktif yapmadan önce 'İçerik incelendi ve onaylandı' işaretlenmelidir.");
      return;
    }
    const statusText = newStatus ? "aktif" : "pasif";
    const ok = window.confirm(`"${item.title || item.topic}" efsanesi ${statusText} yapılsın mı?`);
    if (!ok) return;
    
    setLoading(true);
    try {
      await legendsRepo.toggleActive(item.id, newStatus);
      load();
    } catch (error) {
      console.error("Error toggling legend status:", error);
      alert("Durum değiştirme işlemi başarısız oldu.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("tr-TR");
  };

  const truncateText = (value, maxLen) => {
    const text = String(value ?? "");
    if (!text) return "";
    if (text.length <= maxLen) return text;
    return `${text.slice(0, maxLen)}...`;
  };

  const getViewCount = (item) => {
    const n = Number(item?.viewCount);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const getLikeCount = (item) => {
    const n = Number(item?.likeCount);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const getAnonLikeStorageKey = useCallback(
    (id) => `legend_like_v1:${String(id || "").trim()}`,
    []
  );

  const hasAnonLiked = useCallback(
    (id) => {
      const safeId = String(id || "").trim();
      if (!safeId) return false;
      try {
        return window?.localStorage?.getItem(getAnonLikeStorageKey(safeId)) === "1";
      } catch {
        return false;
      }
    },
    [getAnonLikeStorageKey]
  );

  const setAnonLiked = useCallback(
    (id, liked) => {
      const safeId = String(id || "").trim();
      if (!safeId) return;
      try {
        if (liked) window?.localStorage?.setItem(getAnonLikeStorageKey(safeId), "1");
        else window?.localStorage?.removeItem(getAnonLikeStorageKey(safeId));
      } catch {
        // ignore
      }
    },
    [getAnonLikeStorageKey]
  );

  const bumpLocalViewCount = (id) => {
    if (!id) return;
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, viewCount: getViewCount(it) + 1 } : it
      )
    );
    setLegendPreviewItem((prev) =>
      prev?.id === id ? { ...prev, viewCount: getViewCount(prev) + 1 } : prev
    );
  };

  const applyLocalLikeUpdate = useCallback(
    (id, liked, likeCount) => {
      if (!id) return;
      setItems((prev) =>
        prev.map((it) => {
          if (it.id !== id) return it;
          const prevLikedBy = Array.isArray(it?.likedBy) ? it.likedBy : [];
          const nextLikedBy = likeUid
            ? (liked
                ? Array.from(new Set([...prevLikedBy, likeUid]))
                : prevLikedBy.filter((u) => u !== likeUid))
            : prevLikedBy;
          return { ...it, likeCount, likedBy: nextLikedBy };
        })
      );
      setLegendPreviewItem((prev) => {
        if (prev?.id !== id) return prev;
        const prevLikedBy = Array.isArray(prev?.likedBy) ? prev.likedBy : [];
        const nextLikedBy = likeUid
          ? (liked
              ? Array.from(new Set([...prevLikedBy, likeUid]))
              : prevLikedBy.filter((u) => u !== likeUid))
          : prevLikedBy;
        return { ...prev, likeCount, likedBy: nextLikedBy };
      });
    },
    [likeUid]
  );

  const onToggleLike = useCallback(
    async (item) => {
      const legendId = String(item?.id || "").trim();
      if (!legendId) return;

      const isLoggedIn = Boolean(likeUid);
      const prevLikeCount = getLikeCount(item);
      const alreadyLiked = isLoggedIn
        ? (Array.isArray(item?.likedBy) ? item.likedBy : []).includes(likeUid)
        : hasAnonLiked(legendId);

      if (alreadyLiked) return;

      if (likeBusyById[legendId]) return;
      setLikeBusyById((prev) => ({ ...prev, [legendId]: true }));

      if (!isLoggedIn) setAnonLiked(legendId, true);
      applyLocalLikeUpdate(legendId, isLoggedIn, prevLikeCount + 1);

      try {
        if (isLoggedIn) {
          const result = await legendsRepo.like(legendId, likeUid);
          applyLocalLikeUpdate(legendId, result?.liked === true, Number(result?.likeCount) || 0);
        } else {
          const result = await legendsRepo.likeAnonymous(legendId);
          applyLocalLikeUpdate(legendId, false, Number(result?.likeCount) || prevLikeCount + 1);
        }
      } catch (error) {
        console.error("Error toggling like:", error);
        if (!isLoggedIn) setAnonLiked(legendId, false);
        applyLocalLikeUpdate(legendId, false, prevLikeCount);
        alert("Beğenme işlemi başarısız oldu.");
      } finally {
        setLikeBusyById((prev) => {
          const { [legendId]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [applyLocalLikeUpdate, hasAnonLiked, likeBusyById, likeUid, setAnonLiked]
  );

  const canEdit = (item) => {
    if (isAdmin) return true;
    const ownerUid = item?.createdBy?.uid;
    return !!ownerUid && !!currentUid && ownerUid === currentUid;
  };

  const buildPublicAuthorFromProfile = (profile) => {
    const legacyAll = profile?.publishProfileOnLegends === true;
    const share = {
      photo: profile?.legendSharePhoto === true || legacyAll,
      fullName: profile?.legendShareFullName === true || legacyAll,
      gorev: profile?.legendShareGorev === true || legacyAll,
      email: profile?.legendShareEmail === true || legacyAll,
      phone: profile?.legendSharePhone === true || legacyAll,
      address: profile?.legendShareAddress === true || legacyAll,
    };

    const nameValue = profile?.fullName || profile?.displayName || "";
    const result = {};
    if (share.photo && profile?.photoURL) result.photoURL = profile.photoURL;
    if (share.fullName && nameValue) result.fullName = nameValue;
    if (share.gorev && profile?.gorev) result.gorev = profile.gorev;
    if (share.email && profile?.email) result.email = profile.email;
    if (share.phone && profile?.phone) result.phone = profile.phone;
    if (share.address && profile?.address) result.address = profile.address;
    return Object.keys(result).length > 0 ? result : null;
  };

  const buildPublicAuthorFromLegend = (item) => {
    const pa = item?.publicAuthor;
    if (!pa || typeof pa !== "object") return null;
    const result = {};
    if (pa.photoURL) result.photoURL = pa.photoURL;
    if (pa.fullName) result.fullName = pa.fullName;
    if (pa.gorev) result.gorev = pa.gorev;
    if (pa.email) result.email = pa.email;
    if (pa.phone) result.phone = pa.phone;
    if (pa.address) result.address = pa.address;
    return Object.keys(result).length > 0 ? result : null;
  };

  const openBannerPreview = (item) => {
    setBannerPreviewItem(item);
    setIsBannerPreviewOpen(true);
  };

  const openLegendPreview = async (item) => {
    if (!item) return;
    setLegendPreviewItem(item);
    setIsLegendPreviewOpen(true);
    setLegendPreviewAuthor(null);
    legendPreviewIdRef.current = item.id;

    bumpLocalViewCount(item.id);
    await legendsRepo.incrementViewCount(item.id);

    const publicFromLegend = buildPublicAuthorFromLegend(item);
    if (publicFromLegend) {
      if (legendPreviewIdRef.current === item.id) setLegendPreviewAuthor(publicFromLegend);
      return;
    }

    const ownerUid = item?.createdBy?.uid;
    if (!ownerUid) return;
    try {
      const snap = await getDoc(doc(db, "userProfiles", ownerUid));
      if (!snap.exists()) return;
      const derived = buildPublicAuthorFromProfile(snap.data() || {});
      if (legendPreviewIdRef.current === item.id) setLegendPreviewAuthor(derived);
    } catch {}
  };

  const closeLegendPreview = () => {
    setIsLegendPreviewOpen(false);
    setLegendPreviewItem(null);
    setLegendPreviewAuthor(null);
    legendPreviewIdRef.current = null;
  };

  return (
    <>
      {!embedded ? (
        <div className="header bg-gradient-info pb-6 pt-5 pt-md-7">
          <Container fluid>
            <div className="header-body">
              <div className="mb-3">
                <div
                  className="d-flex justify-content-center"
                  style={{
                    gap: 12,
                    overflowX: "auto",
                    paddingBottom: 4,
                    WebkitOverflowScrolling: "touch",
                    maxWidth: "100%",
                  }}
                >
                  {LEGENDS_BANNER_ITEMS.map((item, idx) => (
                    <button
                      key={item.key}
                      type="button"
                      className="p-0 border-0 bg-transparent"
                      onClick={() => openBannerPreview(item)}
                      aria-label={`Story ${idx + 1}`}
                      style={{ flex: "0 0 auto" }}
                    >
                      <div className="p-1 rounded-circle border border-white">
                        <img
                          src={item.src}
                          alt={item.altText}
                          className="rounded-circle d-block"
                          style={{ width: 56, height: 56, objectFit: "cover" }}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <Row className="align-items-center py-4">
                <Col lg="6" xs="7">
                  <h6 className="h2 text-white d-inline-block mb-0">
                    Karadeniz Efsaneleri
                  </h6>
                </Col>
                <Col lg="6" xs="5" className="text-right">
                  <Button
                    tag={Link}
                    to="/admin/content/legends/new"
                    color="secondary"
                    size="sm"
                  >
                    <span className="font-weight-bold">Yeni Efsane Yaz</span>
                  </Button>
                </Col>
              </Row>
            </div>
          </Container>
        </div>
      ) : null}

      <Container className={embedded ? "mt-4" : "mt--6"} fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <div className="d-flex align-items-center justify-content-between">
                  <h3 className="mb-0">Efsane Listesi</h3>
                  <Button
                    tag={Link}
                    to="/admin/content/legends/new"
                    color="info"
                    outline
                    size="sm"
                    aria-label="Yeni Efsane Ekle"
                    title="Yeni Efsane Ekle"
                    className="font-weight-bold"
                  >
                    <small className="font-weight-bold">Yeni Ekle</small>
                  </Button>
                </div>

                {embedded ? (
                  <>
                    <div className="mt-3 d-flex justify-content-start" style={{ gap: 8, flexWrap: "wrap" }}>
                      <Button
                        type="button"
                        size="sm"
                        color={!selectedProvince && !pendingApprovalFilter ? "primary" : "secondary"}
                        outline={Boolean(selectedProvince) || pendingApprovalFilter}
                        onClick={() => {
                          setSelectedProvince("");
                          setSelectedDistrict("");
                          setPendingApprovalFilter(false);
                        }}
                      >
                        Efsane Listesi ({legendsTotalCount})
                      </Button>
                      {isAdmin ? (
                        <Button
                          type="button"
                          size="sm"
                          style={{
                            backgroundColor: pendingApprovalFilter ? "#dc3545" : "transparent",
                            borderColor: "#dc3545",
                            color: pendingApprovalFilter ? "#fff" : "#dc3545",
                            fontWeight: pendingApprovalFilter ? 700 : 400,
                          }}
                          onClick={() => {
                            setPendingApprovalFilter((prev) => !prev);
                            setSelectedProvince("");
                            setSelectedDistrict("");
                            setCurrentPage(1);
                          }}
                        >
                          Yeni Admin Onayı Bekleyen Efsane Yazıları
                          {" ("}
                          {items.filter((i) => i?.isActive === false).length}
                          {")"}
                        </Button>
                      ) : null}
                    </div>

                    {provinceRows.length ? (
                      <div
                        className="mt-3 d-flex justify-content-start"
                        style={{
                          gap: 8,
                          flexWrap: "nowrap",
                          overflowX: "auto",
                          WebkitOverflowScrolling: "touch",
                          paddingBottom: 4,
                        }}
                      >
                        {provinceRows.map((row) => (
                          <Button
                            key={row.province}
                            type="button"
                            size="sm"
                            color={selectedProvince === row.province ? "primary" : "secondary"}
                            outline={selectedProvince !== row.province}
                            onClick={() => {
                              setSelectedProvince(row.province);
                              setSelectedDistrict("");
                            }}
                            style={{ flex: "0 0 auto" }}
                          >
                            {row.province} ({row.count})
                          </Button>
                        ))}
                      </div>
                    ) : null}

                    {selectedProvince && districtRows.length ? (
                      <div
                        className="mt-2 d-flex justify-content-start"
                        style={{
                          gap: 8,
                          flexWrap: "nowrap",
                          overflowX: "auto",
                          WebkitOverflowScrolling: "touch",
                          paddingBottom: 4,
                        }}
                      >
                        <Button
                          type="button"
                          size="sm"
                          color={!selectedDistrict ? "primary" : "secondary"}
                          outline={Boolean(selectedDistrict)}
                          onClick={() => setSelectedDistrict("")}
                          style={{ flex: "0 0 auto" }}
                        >
                          Tüm İlçeler ({provinceFilteredItems.length})
                        </Button>
                        {districtRows.map((row) => (
                          <Button
                            key={row.districtKey}
                            type="button"
                            size="sm"
                            color={selectedDistrict === row.districtKey ? "primary" : "secondary"}
                            outline={selectedDistrict !== row.districtKey}
                            onClick={() => setSelectedDistrict(row.districtKey)}
                            style={{ flex: "0 0 auto" }}
                          >
                            {row.districtLabel} ({row.count})
                          </Button>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-dark">Yükleniyor...</p>
                  </div>
                ) : (
                  districtFilteredItems.length ? (
                    <>
                      <div className="d-md-none">
                        {pagedItems.map((item) => (
                          <div key={item.id} className="py-3 border-bottom">
                            <div className="d-flex" style={{ gap: 12 }}>
                              <img
                                src={item.imageUrl || LEGEND_FALLBACK_THUMB_SRC}
                                alt={item.imageUrl ? item.title : "Destination Black Sea"}
                                className={item.imageUrl ? "" : "bg-white border"}
                                style={{
                                  width: 44,
                                  height: 44,
                                  objectFit: item.imageUrl ? "cover" : "contain",
                                  borderRadius: 4,
                                  padding: item.imageUrl ? 0 : 6,
                                  flex: "0 0 auto",
                                }}
                              />

                              <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                                <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                  <Button
                                    type="button"
                                    color="info"
                                    outline
                                    size="sm"
                                    onClick={() => openLegendPreview(item)}
                                    aria-label="OKU"
                                    title="OKU"
                                  >
                                    OKU
                                  </Button>
                                  <strong style={{ minWidth: 0 }}>
                                    {truncateText(item.title, 30)}
                                  </strong>
                                </div>

                                <div className="mt-2">
                                  <small className="text-dark mr-2">Medya:</small>
                                  {item.imageUrl || item.audioUrl || item.videoUrl ? (
                                    <span className="d-inline-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                                      {item.imageUrl && (
                                        <Badge color="success" pill>
                                          Görsel
                                        </Badge>
                                      )}
                                      {item.audioUrl && (
                                        <Badge color="info" pill>
                                          Ses
                                        </Badge>
                                      )}
                                      {item.videoUrl && (
                                        <Badge color="warning" pill>
                                          Video
                                        </Badge>
                                      )}
                                    </span>
                                  ) : (
                                    <small className="text-dark">-</small>
                                  )}
                                </div>

                                <small className="text-dark d-block mt-2">
                                  {truncateText(item.story, 30)}
                                </small>

                                <small className="text-dark d-block mt-2">
                                  Okunma: <strong>{getViewCount(item)}</strong>
                                  {" - "}
                                  {item.isActive !== false ? "Aktif" : "Pasif"}
                                  {" - "}
                                  {formatDate(item.createdAt)}
                                </small>

                                <div
                                  className="mt-2 d-flex flex-nowrap"
                                  style={{
                                    gap: 2,
                                    overflowX: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    paddingBottom: 4,
                                  }}
                                >
                                  <Button
                                    tag={Link}
                                    to={`/admin/content/legends/view/${item.id}`}
                                    color="info"
                                    outline
                                    size="sm"
                                    style={LEGEND_ACTION_BUTTON_STYLE}
                                  >
                                    İNCELE
                                  </Button>

                                  <Button
                                    type="button"
                                    color="info"
                                    outline
                                    size="sm"
                                    style={LEGEND_ACTION_BUTTON_STYLE}
                                    onClick={() => onShareLegend(item)}
                                    aria-label="PAYLAŞ"
                                    title="PAYLAŞ"
                                  >
                                    PAYLAŞ
                                  </Button>

                                  <Button
                                    type="button"
                                    color="info"
                                    outline
                                    size="sm"
                                    style={LEGEND_ACTION_BUTTON_STYLE}
                                    disabled={Boolean(likeBusyById[item.id])}
                                    onClick={() => onToggleLike(item)}
                                    aria-label="BEĞEN"
                                    title="BEĞEN"
                                  >
                                    {likeBusyById[item.id]
                                      ? "Yükleniyor..."
                                      : `BEĞEN (${getLikeCount(item)})`}
                                  </Button>
                                </div>

                                {isAdmin ? (
                                  <div className="mt-2 d-flex flex-column" style={{ gap: 8 }}>
                                    {canEdit(item) && (
                                      <Button
                                        tag={Link}
                                        to={`/admin/content/legends/${item.id}`}
                                        color="primary"
                                        size="sm"
                                      >
                                        Düzenle
                                      </Button>
                                    )}
                                    <Button
                                      color={item.isActive !== false ? "warning" : "success"}
                                      size="sm"
                                      disabled={item.isActive === false && item?.approved !== true}
                                      onClick={() => onToggleActive(item)}
                                    >
                                      {item.isActive !== false ? "Pasif Yap" : "Aktif Yap"}
                                    </Button>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="d-none d-md-block">
                        <Table className="align-items-center table-flush" responsive>
                          <thead className="thead-light">
                            <tr>
                              <th scope="col">Efsane Başlığı</th>
                              {isAdmin ? (
                                <th scope="col" className="text-right">
                                  İşlem
                                </th>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {pagedItems.map((item) => (
                              <tr key={item.id}>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={item.imageUrl || LEGEND_FALLBACK_THUMB_SRC}
                                      alt={item.imageUrl ? item.title : "Destination Black Sea"}
                                      className={item.imageUrl ? "" : "bg-white border"}
                                      style={{
                                        width: 44,
                                        height: 44,
                                        objectFit: item.imageUrl ? "cover" : "contain",
                                        borderRadius: 4,
                                        marginRight: 12,
                                        padding: item.imageUrl ? 0 : 6,
                                      }}
                                    />
                                    <div>
                                      <strong>
                                        <Button
                                          type="button"
                                          color="info"
                                          outline
                                          size="sm"
                                          className="mr-2"
                                          onClick={() => openLegendPreview(item)}
                                          aria-label="OKU"
                                          title="OKU"
                                        >
                                          OKU
                                        </Button>
                                        {truncateText(item.title, 30)}
                                      </strong>

                                      <div className="mt-2">
                                        <small className="text-dark mr-2">Medya:</small>
                                        {item.imageUrl || item.audioUrl || item.videoUrl ? (
                                          <span className="d-inline-flex align-items-center flex-wrap" style={{ gap: 6 }}>
                                            {item.imageUrl && (
                                              <Badge color="success" pill>
                                                Görsel
                                              </Badge>
                                            )}
                                            {item.audioUrl && (
                                              <Badge color="info" pill>
                                                Ses
                                              </Badge>
                                            )}
                                            {item.videoUrl && (
                                              <Badge color="warning" pill>
                                                Video
                                              </Badge>
                                            )}
                                          </span>
                                        ) : (
                                          <small className="text-dark">-</small>
                                        )}
                                      </div>
                                      <br />
                                      <small className="text-dark">
                                        {truncateText(item.story, 30)}
                                      </small>

                                      <div className="mt-2">
                                        <small className="text-dark">
                                          Okunma: <strong>{getViewCount(item)}</strong>
                                          {" - "}
                                          {item.isActive !== false ? "Aktif" : "Pasif"}
                                          {" - "}
                                          {formatDate(item.createdAt)}
                                        </small>
                                      </div>

                                      <div className="mt-2 d-flex flex-wrap" style={{ gap: 2 }}>
                                        <Button
                                          tag={Link}
                                          to={`/admin/content/legends/view/${item.id}`}
                                          color="info"
                                          outline
                                          size="sm"
                                          style={LEGEND_ACTION_BUTTON_STYLE}
                                        >
                                          İNCELE
                                        </Button>

                                        <Button
                                          type="button"
                                          color="info"
                                          outline
                                          size="sm"
                                          style={LEGEND_ACTION_BUTTON_STYLE}
                                          onClick={() => onShareLegend(item)}
                                          aria-label="PAYLAŞ"
                                          title="PAYLAŞ"
                                        >
                                          PAYLAŞ
                                        </Button>

                                        <Button
                                          type="button"
                                          color="info"
                                          outline
                                          size="sm"
                                          style={LEGEND_ACTION_BUTTON_STYLE}
                                          disabled={Boolean(likeBusyById[item.id])}
                                          onClick={() => onToggleLike(item)}
                                          aria-label="BEĞEN"
                                          title="BEĞEN"
                                        >
                                          {likeBusyById[item.id]
                                            ? "Yükleniyor..."
                                            : `BEĞEN (${getLikeCount(item)})`}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                {isAdmin ? (
                                  <td className="text-right">
                                    <div className="d-flex flex-column flex-md-row justify-content-end" style={{ gap: 8 }}>
                                      {canEdit(item) && (
                                        <Button
                                          tag={Link}
                                          to={`/admin/content/legends/${item.id}`}
                                          color="primary"
                                          size="sm"
                                          className="w-100 w-md-auto"
                                        >
                                          Düzenle
                                        </Button>
                                      )}
                                      <Button
                                        color={item.isActive !== false ? "warning" : "success"}
                                        size="sm"
                                        className="w-100 w-md-auto"
                                        disabled={item.isActive === false && item?.approved !== true}
                                        onClick={() => onToggleActive(item)}
                                      >
                                        {item.isActive !== false ? "Pasif Yap" : "Aktif Yap"}
                                      </Button>
                                    </div>
                                  </td>
                                ) : null}
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-muted py-4">
                      {selectedProvince && selectedDistrict
                        ? `${selectedProvince} / ${selectedDistrict === "__none__" ? "Belirtilmemiş" : selectedDistrict} için efsane bulunamadı.`
                        : selectedProvince
                          ? `${selectedProvince} için efsane bulunamadı.`
                          : "Efsane bulunamadı."}
                    </div>
                  )
                )}

                {!loading && legendsTotalCount > 0 && districtFilteredItems.length > 0 && (
                  <div className="d-flex justify-content-end" style={{ overflowX: "auto" }}>
                    <Pagination className="mb-0" size="sm">
                      <PaginationItem disabled={currentPage <= 1}>
                        <PaginationLink
                          previous
                          href="#prev"
                          onClick={(e) => {
                            e.preventDefault();
                            goToPage(currentPage - 1);
                          }}
                        />
                      </PaginationItem>

                      {Array.from({ length: totalPages }).map((_, idx) => {
                        const page = idx + 1;
                        return (
                          <PaginationItem key={page} active={page === currentPage}>
                            <PaginationLink
                              href={`#page-${page}`}
                              onClick={(e) => {
                                e.preventDefault();
                                goToPage(page);
                              }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      <PaginationItem disabled={currentPage >= totalPages}>
                        <PaginationLink
                          next
                          href="#next"
                          onClick={(e) => {
                            e.preventDefault();
                            goToPage(currentPage + 1);
                          }}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}

                {!loading && legendsTotalCount === 0 && (
                  <div className="text-center text-dark py-4">
                    Henüz efsane eklenmedi. "Yeni Efsane" butonuyla ekleyin.
                  </div>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={isBannerPreviewOpen}
        toggle={() => setIsBannerPreviewOpen(false)}
        size="lg"
        centered
      >
        <ModalBody className="p-0" style={{ position: "relative" }}>
          <button
            type="button"
            className="close"
            aria-label="Kapat"
            onClick={() => setIsBannerPreviewOpen(false)}
            style={{ position: "absolute", top: 8, right: 12, zIndex: 2 }}
          >
            <span aria-hidden="true">×</span>
          </button>
          <img
            src={bannerPreviewItem?.src}
            alt={bannerPreviewItem?.altText || "Banner"}
            className="img-fluid"
            style={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        </ModalBody>
      </Modal>

      <Modal isOpen={isLegendPreviewOpen} toggle={closeLegendPreview} size="lg" centered>
        <ModalBody className="p-0" style={{ position: "relative" }}>
          <button
            type="button"
            className="close"
            aria-label="Kapat"
            onClick={closeLegendPreview}
            style={{ position: "absolute", top: 8, right: 12, zIndex: 2 }}
          >
            <span aria-hidden="true">×</span>
          </button>

          <div className="p-3 p-md-4">
            <h4 className="mb-2">{legendPreviewItem?.title || "Efsane"}</h4>

            <div className="mb-3">
              <small className="text-dark">
                Okunma: <strong>{getViewCount(legendPreviewItem)}</strong>
              </small>


              <div className="mt-2 d-flex flex-nowrap" style={{ gap: 2 }}>
                <Button
                  type="button"
                  color="info"
                  outline
                  size="sm"
                  style={LEGEND_ACTION_BUTTON_STYLE}
                  onClick={() => onShareLegend(legendPreviewItem)}
                  aria-label="PAYLAŞ"
                  title="PAYLAŞ"
                >
                  PAYLAŞ
                </Button>

                <Button
                  type="button"
                  color="info"
                  outline
                  size="sm"
                  style={LEGEND_ACTION_BUTTON_STYLE}
                  disabled={Boolean(likeBusyById[legendPreviewItem?.id])}
                  onClick={() => onToggleLike(legendPreviewItem)}
                  aria-label="BEĞEN"
                  title="BEĞEN"
                >
                  {likeBusyById[legendPreviewItem?.id]
                    ? "Yükleniyor..."
                    : `BEĞEN (${getLikeCount(legendPreviewItem)})`}
                </Button>
              </div>
            </div>

            <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
              {legendPreviewItem?.story || "-"}
            </div>

            <div className="mb-2">
              <small className="text-dark">Medya</small>
            </div>

            {legendPreviewItem?.imageUrl ? (
              <div className="mb-3">
                <img
                  src={legendPreviewItem.imageUrl}
                  alt={legendPreviewItem.title || "Görsel"}
                  className="img-fluid rounded"
                  style={{ width: "100%", maxHeight: 420, objectFit: "contain" }}
                />
              </div>
            ) : null}

            {legendPreviewItem?.audioUrl ? (
              <div className="mb-3">
                <audio controls style={{ width: "100%" }}>
                  <source src={legendPreviewItem.audioUrl} />
                  Tarayıcınız ses oynatmayı desteklemiyor.
                </audio>
              </div>
            ) : null}

            {legendPreviewItem?.videoUrl ? (
              <div className="mb-3">
                <video controls style={{ width: "100%", maxHeight: 420 }}>
                  <source src={legendPreviewItem.videoUrl} />
                  Tarayıcınız video oynatmayı desteklemiyor.
                </video>
              </div>
            ) : null}

            {legendPreviewItem?.videoLink ? (
              <div className="mb-3">
                <small className="text-dark d-block">Video Linki</small>
                <a href={legendPreviewItem.videoLink} target="_blank" rel="noreferrer">
                  {legendPreviewItem.videoLink}
                </a>
              </div>
            ) : null}

            {!legendPreviewItem?.imageUrl &&
            !legendPreviewItem?.audioUrl &&
            !legendPreviewItem?.videoUrl &&
            !legendPreviewItem?.videoLink ? (
              <div className="text-dark">Bu efsaneye ait medya bulunmuyor.</div>
            ) : null}

            {legendPreviewAuthor ? (
              <>
                <hr className="my-4" />
                <h6 className="text-dark mb-3">Efsaneyi Yazan</h6>

                {legendPreviewAuthor.photoURL ? (
                  <div className="mb-3 text-center">
                    <img
                      src={legendPreviewAuthor.photoURL}
                      alt="Yazar"
                      className="rounded-circle"
                      style={{ width: 72, height: 72, objectFit: "cover" }}
                    />
                  </div>
                ) : null}

                {legendPreviewAuthor.fullName ? (
                  <div className="mb-2">
                    <strong>Adı Soyadı:</strong> {legendPreviewAuthor.fullName}
                  </div>
                ) : null}
                {legendPreviewAuthor.gorev ? (
                  <div className="mb-2">
                    <strong>Görevi:</strong> {legendPreviewAuthor.gorev}
                  </div>
                ) : null}
                {legendPreviewAuthor.email ? (
                  <div className="mb-2">
                    <strong>E-posta:</strong> {legendPreviewAuthor.email}
                  </div>
                ) : null}
                {legendPreviewAuthor.phone ? (
                  <div className="mb-2">
                    <strong>Telefon:</strong> {legendPreviewAuthor.phone}
                  </div>
                ) : null}
                {legendPreviewAuthor.address ? (
                  <div className="mb-0">
                    <strong>Adres:</strong> {legendPreviewAuthor.address}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
